import { BASE_URL } from "@config/constants";
import { MeetingType } from "@enums/meetings.enum";

/** Query string that opens the meetings list with every type visible. */
export const meetingParams =
  "type=Select+all&type=Investment+Committee&type=Pipeline+Review&type=Meeting";

const openTab = (path: string): void => {
  window.open(`${BASE_URL}${path}`, "_blank");
};

export const openDealPage = (id: string) => openTab(`deal/${id}`);
export const openCompanyPage = (id: string) => openTab(`company/${id}`);
export const openPersonPage = (id: string) => openTab(`person/${id}`);
export const openNotePage = (id: string) => openTab(`notes/search?n=${id}&nm=full`);
export const openThemePage = (id: string) => openTab(`sourcing/${id}`);
export const openFundPage = (id: string) => openTab(`fundraising/${id}`);
export const openOpportunityPage = (id: string) => openTab(`opportunities/${id}`);
export const openThreadPage = (id: string) => openTab(`scout/?thread=${id}`);

/** Documents live under one path — folders and files-by-id both open here. */
export const openDocumentPage = (id: string) => openTab(`documents/${id}`);

/** Opens the documents list filtered by a search query (used to surface a file by name). */
export const openDocumentSearch = (query: string) =>
  openTab(`documents?limit=50&offset=0&search=${encodeURIComponent(query)}`);

/** Opens the meetings list, optionally focused on a specific meeting. */
export const openMeetingsList = (meetingId?: string) =>
  openTab(`meetings?${meetingParams}${meetingId ? `&meeting=${meetingId}` : ""}`);

/** Regular meetings open in the list view; IC / Pipeline meetings have a dedicated page. */
export const openMeetingPage = (id: string, meetingType?: string) =>
  meetingType === MeetingType.MEETING ? openMeetingsList(id) : openTab(`meeting/${id}`);

export type NavigableEntityType =
  | "deal"
  | "company"
  | "person"
  | "note"
  | "theme"
  | "fund"
  | "opportunity"
  | "folder"
  | "document"
  | "meeting"
  | "thread";

export interface NavigableEntity {
  type: NavigableEntityType | string;
  id: string;
  /** For `document` — the name used for the documents search fallback. */
  searchQuery?: string;
  /** For `meeting` — decides between the list view and the dedicated page. */
  meetingType?: string;
}

/** Opens the Meridian page for any navigable entity — the shared dispatcher for tags and mentions. */
export function openEntityInNewTab(entity: NavigableEntity): void {
  switch (entity.type) {
    case "deal":
      return openDealPage(entity.id);
    case "company":
      return openCompanyPage(entity.id);
    case "person":
      return openPersonPage(entity.id);
    case "note":
      return openNotePage(entity.id);
    case "theme":
      return openThemePage(entity.id);
    case "fund":
      return openFundPage(entity.id);
    case "opportunity":
      return openOpportunityPage(entity.id);
    case "folder":
      return openDocumentPage(entity.id);
    case "document":
      return openDocumentSearch(entity.searchQuery ?? "");
    case "meeting":
      return openMeetingPage(entity.id, entity.meetingType);
    case "thread":
      return openThreadPage(entity.id);
    default:
      return;
  }
}
